import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Divider,
    TextField,
    Box,
    Typography
} from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { getTextColor } from "../../utils/colorUtils";

const TypeToConfirmDialog = ({
    open,
    title,
    content,
    confirmationWord = "cancel",
    onConfirm,
    onCancel
}) => {
    const theme = useTheme();
    const [inputValue, setInputValue] = useState('');
    const [isValid, setIsValid] = useState(false);
    const inputRef = useRef(null);

    // Reset input when dialog opens/closes and handle focus
    useEffect(() => {
        if (open) {
            setInputValue('');
            setIsValid(false);
            // Focus the input after a short delay to ensure dialog is fully rendered
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [open]);

    // Check if input matches confirmation word
    useEffect(() => {
        setIsValid(inputValue.toLowerCase() === confirmationWord.toLowerCase());
    }, [inputValue, confirmationWord]);

    const handleInputChange = (event) => {
        setInputValue(event.target.value);
    };

    const handleConfirm = () => {
        if (isValid) {
            onConfirm();
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && isValid) {
            handleConfirm();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            PaperProps={{ sx: { borderRadius: "8px", backgroundColor: theme.palette.background.default } }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title" sx={{ fontSize: theme.typography.h5.fontSize }}>
                {title}
            </DialogTitle>
            <Divider sx={{ marginRight: 2, marginLeft: 2 }} />
            <DialogContent>
                <DialogContentText
                    id="alert-dialog-description"
                    sx={{ color: getTextColor(theme.palette.background.default), mb: 2 }}
                >
                    {content}
                </DialogContentText>
                <Box sx={{ mt: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ mb: 1, color: getTextColor(theme.palette.background.default) }}
                    >
                        To confirm this action, type <strong>"{confirmationWord}"</strong> in the box below:
                    </Typography>
                    <TextField
                        ref={inputRef}
                        variant="outlined"
                        size="small"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyPress={handleKeyPress}
                        placeholder={`Type "${confirmationWord}" here`}
                        error={inputValue.length > 0 && !isValid}
                        helperText={
                            inputValue.length > 0 && !isValid
                                ? `Please type "${confirmationWord}" exactly as shown`
                                : ''
                        }
                        sx={{
                            width: { xs: '75%', sm: '60%', md: '50%' }, // Responsive width
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-error': {
                                    '& fieldset': {
                                        borderColor: theme.palette.error.main,
                                    },
                                },
                                '&.Mui-focused': {
                                    '& fieldset': {
                                        borderColor: isValid ? theme.palette.success.main : theme.palette.primary.main,
                                    },
                                },
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onCancel}
                    size="small"
                    variant="outlined"
                    color="black"
                    sx={{ mb: 2 }}
                >
                    close
                </Button>
                <Button
                    onClick={handleConfirm}
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={!isValid}
                    sx={{
                        mb: 2,
                        mr: 2,
                        opacity: isValid ? 1 : 0.6,
                        cursor: isValid ? 'pointer' : 'not-allowed'
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TypeToConfirmDialog;